/* ==========================================================================
   Studio calendar — every upcoming public session at one studio
   --------------------------------------------------------------------------
   Rendered by sections/studio-calendar.liquid, which writes the studio's
   workshops (id, url, name, image, whether BookThatApp manages it) and the
   copy into a JSON block marked data-sc-config.

   Data: BookThatApp's frames feed through the Shopify App Proxy, the same
   read-only endpoint the MAS booking widget uses (same-origin on every
   storefront domain, no token):

     GET /apps/bookthatapp/api/v1/frames?external_id=ID,ID,…&start=&finish=&shop=
         -> { frames: [{ date, timeslots: [{ id, external_id, start, finish,
              resource_availability, availability: [{ status, available }] }] }],
              first_occurrence, next_occurrence, last_occurrence }

   Every workshop is asked for in ONE request (comma-separated ids; each
   timeslot carries its product's external_id). If that request fails, each
   workshop is asked for on its own, so one bad product costs only its own
   sessions. The whole horizon is loaded up front — today to the end of the
   week holding the last day of the "weeks ahead" month — so week and month
   navigation, the filter and "next session" never wait on the network.
   Results are kept in sessionStorage for a few minutes.

   Nothing here reserves, holds or adds to cart. Booking stays on the
   workshop page.

   All dates and times are shown in the studio's time zone, never the
   visitor's. Calendar days are handled as "YYYY-MM-DD" strings with UTC
   arithmetic, so a DST change can never shift a session to another day.
   ========================================================================== */
(function () {
  'use strict';

  var CACHE_TTL_MS = 5 * 60 * 1000;
  var CACHE_PREFIX = 'mas-studio-cal:v1:';
  // Same reasoning as the booking widget's FRAMES_TIMEOUT_MS: the endpoint
  // answers in well under a second, but a healthy request can queue behind the
  // rest of the page. This only catches one that is never coming back.
  var FETCH_TIMEOUT_MS = 30000;
  var COLORS = 8;          // --sc-c1 … --sc-c8 in the section stylesheet
  var PILLS_PER_DAY = 3;   // month view, desktop
  var DOTS_PER_DAY = 4;    // month view, phones
  var LOW_SEATS = 3;       // "2 spots left" turns amber at or below this

  // Legacy zone names still used as product tags. Modern browsers accept
  // them, but map them anyway so an older ICU can't fall over on them.
  var TZ_ALIASES = {
    'US/Eastern': 'America/New_York',
    'US/Central': 'America/Chicago',
    'US/Mountain': 'America/Denver',
    'US/Arizona': 'America/Phoenix',
    'US/Pacific': 'America/Los_Angeles',
    'US/Alaska': 'America/Anchorage',
    'US/Hawaii': 'Pacific/Honolulu'
  };

  /* ---------- small helpers ------------------------------------------- */

  function qs(root, sel) { return root.querySelector(sel); }
  function qsa(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }
  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }
  function pad2(n) { return (n < 10 ? '0' : '') + n; }
  // "[n] spots left" + { n: 3 } -> "3 spots left"
  function fill(str, map) {
    return String(str == null ? '' : str).replace(/\[(\w+)\]/g, function (m, k) {
      return map && map[k] != null ? String(map[k]) : m;
    });
  }
  function reducedMotion() {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; }
  }
  function isPhone() {
    try { return window.matchMedia('(max-width: 749px)').matches; } catch (e) { return false; }
  }

  // Same funnel as the booking widget: dataLayer, plus Microsoft Clarity
  // with the mas_ prefix so the events sort together there.
  function track(evt, data) {
    try {
      window.dataLayer = window.dataLayer || [];
      var payload = { event: evt };
      if (data) for (var k in data) payload[k] = data[k];
      window.dataLayer.push(payload);
    } catch (e) { /* analytics must never break the calendar */ }
    try {
      window.clarity = window.clarity || function () {
        (window.clarity.q = window.clarity.q || []).push(arguments);
      };
      window.clarity('event', 'mas_' + evt);
    } catch (e) { /* analytics must never break the calendar */ }
  }

  /* ---------- calendar days: "YYYY-MM-DD", no time zone involved ------- */

  function dkey(y, m, d) { return y + '-' + pad2(m) + '-' + pad2(d); }
  function dparts(k) { var a = k.split('-'); return [+a[0], +a[1], +a[2]]; }
  function utcNoon(k) { var a = dparts(k); return new Date(Date.UTC(a[0], a[1] - 1, a[2], 12)); }
  function fromUTC(t) { return dkey(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate()); }
  function addDays(k, n) { var a = dparts(k); return fromUTC(new Date(Date.UTC(a[0], a[1] - 1, a[2] + n))); }
  function addMonths(k, n) { var a = dparts(k); return fromUTC(new Date(Date.UTC(a[0], a[1] - 1 + n, 1))); }
  function weekday(k) { return utcNoon(k).getUTCDay(); }            // 0 = Sunday
  function weekOf(k) { return addDays(k, -weekday(k)); }             // weeks start on Sunday, like the booking widget
  function monthOf(k) { return k.slice(0, 8) + '01'; }
  function lastOfMonth(k) { var a = dparts(k); return fromUTC(new Date(Date.UTC(a[0], a[1], 0))); }
  // Formats a calendar day. The day is pinned to noon UTC and formatted in
  // UTC, so the result is that day whatever the visitor's own zone is.
  function fmtDay(k, opts) {
    var o = { timeZone: 'UTC' };
    for (var p in opts) o[p] = opts[p];
    return new Intl.DateTimeFormat('en-US', o).format(utcNoon(k));
  }

  /* ---------- the studio's clock --------------------------------------- */

  function safeZone(tz) {
    var z = TZ_ALIASES[tz] || tz || 'America/New_York';
    try { new Intl.DateTimeFormat('en-US', { timeZone: z }); return z; } catch (e) { return 'America/New_York'; }
  }
  function partsIn(date, tz) {
    var out = {};
    new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
    }).formatToParts(date).forEach(function (p) { out[p.type] = p.value; });
    var h = parseInt(out.hour, 10); if (h === 24) h = 0;
    return { year: +out.year, month: +out.month, day: +out.day, hour: h, minute: +out.minute };
  }
  // The calendar day an instant falls on at the studio.
  function dayIn(date, tz) { var p = partsIn(date, tz); return dkey(p.year, p.month, p.day); }
  // The instant a studio calendar day begins (the booking widget's method:
  // measure the zone's offset at UTC midnight and shift by it).
  function startOfDay(k, tz) {
    var a = dparts(k);
    var guess = Date.UTC(a[0], a[1] - 1, a[2]);
    var p = partsIn(new Date(guess), tz);
    var offset = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute) - guess;
    return new Date(guess - offset);
  }
  function fmtTime(iso, tz) {
    return new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' }).format(new Date(iso));
  }
  // "10 AM", "2:30 PM" — for the tight month cells.
  function fmtShortTime(iso, tz) {
    var p = partsIn(new Date(iso), tz);
    var h = p.hour % 12 || 12;
    return h + (p.minute ? ':' + pad2(p.minute) : '') + (p.hour < 12 ? ' AM' : ' PM');
  }
  // "2½ hrs", "2 hrs", "90 min" -> "1½ hrs", "45 min"
  function fmtDuration(ms) {
    var mins = Math.round(ms / 60000);
    if (!(mins > 0)) return '';
    if (mins < 60) return mins + ' min';
    var h = Math.floor(mins / 60), r = mins % 60;
    if (r === 0) return h + (h === 1 ? ' hr' : ' hrs');
    if (r === 30) return h + '½ hrs';
    return h + ' hr ' + r + ' min';
  }
  // "Eastern Time" where the browser knows the generic name, else "EDT", else the zone id.
  function zoneName(tz) {
    var styles = ['longGeneric', 'short'];
    for (var i = 0; i < styles.length; i++) {
      try {
        var parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: styles[i] }).formatToParts(new Date());
        for (var j = 0; j < parts.length; j++) if (parts[j].type === 'timeZoneName') return parts[j].value;
      } catch (e) { /* style not supported here, try the next */ }
    }
    return tz;
  }

  /* ---------- BookThatApp data ----------------------------------------- */

  // Open seats in a timeslot, for all three BTA capacity modes — the same
  // rule as slotSeats() in assets/mas-booking-widget.js:
  //   resource-level -> resource_availability is the shared room capacity
  //   product/variant-level -> the best `available` among open variants
  function seatsOf(slot) {
    var pool = (typeof slot.resource_availability === 'number') ? slot.resource_availability : null;
    var anyOpen = false, maxAvail = 0;
    (slot.availability || []).forEach(function (a) {
      var n = (a.status === 1) ? (a.available || 0) : 0;
      if (pool !== null) n = Math.min(n, pool);
      if (n > 0) anyOpen = true;
      if (n > maxAvail) maxAvail = n;
    });
    if (pool === null) pool = maxAvail;
    return { pool: Math.max(0, pool), open: anyOpen && pool > 0 };
  }

  function getJSON(url) {
    var opts = { headers: { Accept: 'application/json' }, credentials: 'same-origin' };
    var ctrl = (typeof AbortController === 'function') ? new AbortController() : null;
    var timer = null;
    if (ctrl) {
      opts.signal = ctrl.signal;
      timer = setTimeout(function () { ctrl.abort(); }, FETCH_TIMEOUT_MS);
    }
    return fetch(url, opts).then(function (r) {
      clearTimeout(timer);
      if (!r.ok) throw new Error('frames HTTP ' + r.status);
      return r.json();
    }, function (e) {
      clearTimeout(timer);
      throw (e && e.name === 'AbortError') ? new Error('frames timed out') : e;
    });
  }

  function cacheRead(key) {
    try {
      var raw = window.sessionStorage.getItem(key);
      if (!raw) return null;
      var v = JSON.parse(raw);
      if (!v || !v.d || Date.now() - v.t > CACHE_TTL_MS) return null;
      return v.d;
    } catch (e) { return null; }
  }
  function cacheWrite(key, data) {
    try { window.sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), d: data })); } catch (e) { /* private mode, full, blocked: fine */ }
  }

  /* ---------- the calendar -------------------------------------------- */

  function StudioCalendar(root) {
    var node = qs(root, '[data-sc-config]');
    var cfg = null;
    try { cfg = JSON.parse(node.textContent); } catch (e) { cfg = null; }
    if (!cfg) return;

    var self = this;
    this.root = root;
    this.cfg = cfg;
    this.text = cfg.text || {};
    this.tz = safeZone(cfg.timezone);
    this.anchor = cfg.anchor ? '#' + String(cfg.anchor).replace(/^#/, '') : '';
    this.products = (cfg.products || []).map(function (p, i) {
      return { id: String(p.id), handle: p.handle, url: p.url, name: p.name, image: p.image, imageLg: p.imageLg, price: p.price, bta: !!p.bta, color: (i % COLORS) + 1 };
    });
    this.byId = {};
    this.products.forEach(function (p) { self.byId[p.id] = p; });
    // Ask BookThatApp only about products it manages; if none are flagged
    // (the metafield went missing), ask about all of them.
    var managed = this.products.filter(function (p) { return p.bta; });
    this.queried = (managed.length ? managed : this.products).filter(function (p) { return /^\d+$/.test(p.id); });

    var weeks = Math.min(52, Math.max(1, parseInt(cfg.weeksAhead, 10) || 8));
    this.today = dayIn(new Date(), this.tz);
    var horizonDay = addDays(this.today, weeks * 7);
    // Load to the end of the week that holds the last day of the horizon's
    // month: every week and month the arrows can reach is then complete.
    this.lastDay = addDays(weekOf(lastOfMonth(horizonDay)), 6);
    this.minWeek = weekOf(this.today);
    this.maxWeek = weekOf(this.lastDay);
    this.minMonth = monthOf(this.today);
    this.maxMonth = monthOf(horizonDay);

    this.state = {
      view: cfg.view === 'month' ? 'month' : 'week',
      focus: this.today,       // any day inside the visible week / month
      filter: null,            // product id, or null for every workshop
      selected: null,          // month view: the day whose sessions are listed
      status: 'loading',       // loading | ready | error
      sessions: [],
      failed: []
    };

    this.ui = {
      wrap: qs(root, '[data-sc-ui]'),
      views: qsa(root, '[data-sc-view]'),
      prev: qs(root, '[data-sc-prev]'),
      next: qs(root, '[data-sc-next]'),
      today: qs(root, '[data-sc-today]'),
      label: qs(root, '[data-sc-label]'),
      chips: qs(root, '[data-sc-chips]'),
      status: qs(root, '[data-sc-status]'),
      body: qs(root, '[data-sc-body]'),
      note: qs(root, '[data-sc-note]'),
      zone: qs(root, '[data-sc-zone]'),
      fallback: qs(root, '[data-sc-fallback]')
    };
    this.bodyId = 'sc-body-' + Math.random().toString(36).slice(2, 8);
    this.ui.body.id = this.bodyId;

    this.bind();
    this.render();
    this.load();
  }

  StudioCalendar.prototype.bind = function () {
    var self = this;
    this.ui.views.forEach(function (b) {
      b.addEventListener('click', function () { self.setView(b.getAttribute('data-sc-view')); });
    });
    this.ui.prev.addEventListener('click', function () { self.shift(-1); });
    this.ui.next.addEventListener('click', function () { self.shift(1); });
    this.ui.today.addEventListener('click', function () {
      self.state.focus = self.today;
      self.state.selected = null;
      self.render();
    });
    this.ui.chips.addEventListener('click', function (e) {
      var b = e.target.closest ? e.target.closest('[data-sc-chip]') : null;
      if (b) self.setFilter(b.getAttribute('data-sc-chip') || null);
    });
    this.ui.body.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      var day = e.target.closest('[data-sc-day]');
      if (day) { self.selectDay(day.getAttribute('data-sc-day')); return; }
      var jump = e.target.closest('[data-sc-jump]');
      if (jump) { self.jumpTo(jump.getAttribute('data-sc-jump')); return; }
      var all = e.target.closest('[data-sc-show-all]');
      if (all) { self.setFilter(null); self.ui.label.focus(); return; }
      var link = e.target.closest('[data-sc-session]');
      if (link) {
        var p = self.byId[link.getAttribute('data-sc-session')];
        track('studio_calendar_session_click', { workshop: p ? p.handle : '', session_date: link.getAttribute('data-sc-date') || '', view: self.state.view });
      }
    });
  };

  /* ---------- loading --------------------------------------------------- */

  StudioCalendar.prototype.framesURL = function (ids, startISO, finishISO) {
    var base = this.cfg.proxy || '/apps/bookthatapp';
    // Ids are digits only (checked in the constructor), so the commas go in
    // raw — the form BookThatApp was verified to accept.
    return base + '/api/v1/frames?external_id=' + ids.join(',') +
      '&start=' + encodeURIComponent(startISO) +
      '&finish=' + encodeURIComponent(finishISO) +
      '&shop=' + encodeURIComponent(this.cfg.shop || '');
  };

  // Flattens one frames response into the calendar's own session records.
  StudioCalendar.prototype.flatten = function (data) {
    var self = this, out = [], seen = {}, now = Date.now();
    ((data && data.frames) || []).forEach(function (f) {
      (f.timeslots || []).forEach(function (s) {
        var pid = String(s.external_id != null ? s.external_id : '');
        if (!self.byId[pid]) return;                                  // not one of this studio's workshops
        var t = new Date(s.start).getTime();
        if (!(t > now)) return;                                       // already started
        var k = pid + ':' + s.id;
        if (seen[k]) return;
        seen[k] = 1;
        var seats = seatsOf(s);
        out.push({
          pid: pid, id: s.id, start: s.start, finish: s.finish,
          pool: seats.pool, open: seats.open,
          day: dayIn(new Date(s.start), self.tz)                      // the day AT THE STUDIO, not in UTC
        });
      });
    });
    return out;
  };

  // BookThatApp answers 422 "Date range exceeds maximum of 90 days" to a
  // longer frames request, and weeks-ahead plus rounding out to whole months
  // easily passes that. So the horizon is asked for in windows of at most
  // MAX_RANGE_DAYS, in parallel. The first window (the dates people look at
  // first) must load; a later one that fails just shortens the calendar, and
  // the answer is then not cached.
  var MAX_RANGE_DAYS = 85;

  StudioCalendar.prototype.fetchAll = function (ids, startISO, finishISO) {
    var self = this, windows = [];
    var from = new Date(startISO).getTime(), end = new Date(finishISO).getTime();
    while (from <= end) {
      var to = Math.min(end, from + MAX_RANGE_DAYS * 864e5 - 1000);
      windows.push([new Date(from).toISOString(), new Date(to).toISOString()]);
      from = to + 1000;
    }
    return Promise.all(windows.map(function (w, i) {
      var job = self.fetchWindow(ids, w[0], w[1]);
      return i === 0 ? job : job.catch(function () { return { sessions: [], failed: [], partial: true }; });
    })).then(function (parts) {
      var failed = {};
      parts.forEach(function (part) { part.failed.forEach(function (id) { failed[id] = 1; }); });
      return {
        sessions: [].concat.apply([], parts.map(function (part) { return part.sessions; })),
        failed: Object.keys(failed),
        partial: parts.some(function (part) { return part.partial; })
      };
    });
  };

  StudioCalendar.prototype.fetchWindow = function (ids, startISO, finishISO) {
    var self = this;
    return getJSON(this.framesURL(ids, startISO, finishISO)).then(function (data) {
      return { sessions: self.flatten(data), failed: [] };
    }).catch(function (batchErr) {
      if (window.console) console.warn('[MAS studio calendar] combined request failed, asking per workshop', batchErr);
      if (ids.length < 2) throw batchErr;
      return Promise.all(ids.map(function (id) {
        return getJSON(self.framesURL([id], startISO, finishISO)).then(
          function (data) { return { id: id, sessions: self.flatten(data) }; },
          function () { return { id: id, failed: true }; }
        );
      })).then(function (results) {
        var ok = results.filter(function (r) { return !r.failed; });
        if (!ok.length) throw batchErr;
        return {
          sessions: [].concat.apply([], ok.map(function (r) { return r.sessions; })),
          failed: results.filter(function (r) { return r.failed; }).map(function (r) { return r.id; })
        };
      });
    });
  };

  StudioCalendar.prototype.load = function () {
    var self = this, st = this.state;
    var ids = this.queried.map(function (p) { return p.id; });
    if (!ids.length) { st.status = 'error'; this.render(); return Promise.resolve(); }

    var startISO = startOfDay(this.today, this.tz).toISOString();
    var finishISO = new Date(startOfDay(addDays(this.lastDay, 1), this.tz).getTime() - 1000).toISOString();
    var cacheKey = CACHE_PREFIX + (this.cfg.shop || '') + ':' + ids.join(',') + ':' + this.today + ':' + this.lastDay;
    var cached = cacheRead(cacheKey);
    var t0 = Date.now();

    var job = cached
      ? Promise.resolve(cached)
      : this.fetchAll(ids, startISO, finishISO).then(function (res) {
          if (!res.failed.length && !res.partial) cacheWrite(cacheKey, res);   // never cache a partial answer
          return res;
        });

    return job.then(function (res) {
      var now = Date.now();
      st.sessions = (res.sessions || [])
        .filter(function (s) { return new Date(s.start).getTime() > now; })   // cached copies age
        .sort(function (a, b) { return new Date(a.start) - new Date(b.start) || (a.pid < b.pid ? -1 : 1); });
      st.failed = res.failed || [];
      st.status = 'ready';
      self.announce();
      track('studio_calendar_loaded', { sessions: st.sessions.length, cached: !!cached, ms: Date.now() - t0 });
    }, function (e) {
      st.status = 'error';
      if (window.console) console.error('[MAS studio calendar] could not load sessions', e);
    }).then(function () {
      self.buildChips();
      self.render();
    });
  };

  /* ---------- sharing what loaded ---------------------------------------- */

  // Tell the rest of the page what loaded: the workshop cards show each
  // workshop's next open session from this. Kept on window as well, for any
  // listener that registers after the event fires.
  StudioCalendar.prototype.announce = function () {
    var detail = { tz: this.tz, sessions: this.state.sessions };
    window.__masStudioSessions = detail;
    try { document.dispatchEvent(new CustomEvent('mas:studio-sessions', { detail: detail })); } catch (e) { /* old browser */ }
    if (this.cfg.events) this.emitEvents();
  };

  // schema.org Event data for the next sessions, so search and answer engines
  // can list "mosaic lamp workshop, Sat Sep 26, Voorhees" directly. Google
  // reads structured data added by script. Capped, and skips full sessions.
  var EVENTS_MAX = 20;
  StudioCalendar.prototype.emitEvents = function () {
    var cfg = this.cfg, place = cfg.place, self = this;
    if (!place || this.root.__masEventsLd) return;
    var events = this.state.sessions.filter(function (s) { return s.open !== 0; }).slice(0, EVENTS_MAX).map(function (s) {
      var p = self.byId[s.pid] || {};
      var url = location.origin + p.url;
      var ev = {
        '@type': 'Event',
        name: p.name + ' workshop in ' + place.locality + ', ' + place.region,
        startDate: s.start,
        endDate: s.finish,
        eventStatus: 'https://schema.org/EventScheduled',
        eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
        location: {
          '@type': 'Place',
          name: place.name,
          address: {
            '@type': 'PostalAddress',
            streetAddress: place.street,
            addressLocality: place.locality,
            addressRegion: place.region,
            postalCode: place.postal,
            addressCountry: 'US'
          }
        },
        organizer: { '@type': 'Organization', name: cfg.organizer.name, url: cfg.organizer.url },
        url: url,
        description: 'Hands-on ' + p.name + ' workshop at ' + place.name + '. No experience needed; materials and instruction included.'
      };
      if (p.imageLg) ev.image = [p.imageLg];
      if (p.price != null) {
        ev.offers = {
          '@type': 'Offer',
          url: url,
          price: p.price,
          priceCurrency: cfg.currency || 'USD',
          availability: s.open != null && s.open <= LOW_SEATS ? 'https://schema.org/LimitedAvailability' : 'https://schema.org/InStock'
        };
      }
      return ev;
    });
    if (!events.length) return;
    var tag = document.createElement('script');
    tag.type = 'application/ld+json';
    tag.textContent = JSON.stringify({ '@context': 'https://schema.org', '@graph': events });
    this.root.appendChild(tag);
    this.root.__masEventsLd = tag;
  };

  /* ---------- navigation ------------------------------------------------ */

  StudioCalendar.prototype.range = function () {
    if (this.state.view === 'week') {
      var ws = weekOf(this.state.focus);
      return { start: ws, end: addDays(ws, 6) };
    }
    var ms = monthOf(this.state.focus);
    return { start: ms, end: lastOfMonth(ms) };
  };

  // Keeps the view between this week / month and the end of the horizon.
  StudioCalendar.prototype.clamp = function () {
    var st = this.state;
    if (st.view === 'week') {
      if (weekOf(st.focus) < this.minWeek) st.focus = this.today;
      if (weekOf(st.focus) > this.maxWeek) st.focus = this.maxWeek;
    } else {
      if (monthOf(st.focus) < this.minMonth) st.focus = this.today;
      if (monthOf(st.focus) > this.maxMonth) st.focus = this.maxMonth;
    }
  };

  StudioCalendar.prototype.canShift = function (delta) {
    var st = this.state;
    if (st.view === 'week') return delta < 0 ? weekOf(st.focus) > this.minWeek : weekOf(st.focus) < this.maxWeek;
    return delta < 0 ? monthOf(st.focus) > this.minMonth : monthOf(st.focus) < this.maxMonth;
  };

  StudioCalendar.prototype.shift = function (delta) {
    if (!this.canShift(delta)) return;
    var st = this.state;
    st.focus = st.view === 'week' ? addDays(weekOf(st.focus), 7 * delta) : addMonths(monthOf(st.focus), delta);
    st.selected = null;
    this.render();
  };

  StudioCalendar.prototype.setView = function (view) {
    var st = this.state;
    if (view !== 'week' && view !== 'month') return;
    if (view === st.view) return;
    if (view === 'week') {
      // Land on the week of the day being looked at: the chosen day, else
      // the month's first session, else the start of the month.
      var r = this.range();
      var first = this.visible(r)[0];
      var base = st.selected || (first && first.day) || r.start;
      st.focus = base < this.today ? this.today : base;
    } else {
      var ws = weekOf(st.focus);
      st.focus = ws < this.today ? this.today : ws;
      st.selected = null;
    }
    st.view = view;
    track('studio_calendar_view', { view: view });
    this.render();
  };

  StudioCalendar.prototype.setFilter = function (id) {
    var st = this.state;
    st.filter = (id && this.byId[id]) ? id : null;
    st.selected = null;
    qsa(this.ui.chips, '[data-sc-chip]').forEach(function (b) {
      b.setAttribute('aria-pressed', String((b.getAttribute('data-sc-chip') || null) === st.filter));
    });
    track('studio_calendar_filter', { workshop: st.filter ? this.byId[st.filter].handle : 'all' });
    this.render();
  };

  StudioCalendar.prototype.selectDay = function (k) {
    this.state.selected = k;
    this.render();
    var btn = qs(this.ui.body, '[data-sc-day="' + k + '"]');
    if (btn) btn.focus();
    var panel = qs(this.ui.body, '.studio-calendar__panel');
    if (panel && isPhone() && panel.scrollIntoView) panel.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'nearest' });
  };

  StudioCalendar.prototype.jumpTo = function (k) {
    this.state.focus = k;
    this.state.selected = this.state.view === 'month' ? k : null;
    this.render();
    this.ui.label.focus();
  };

  /* ---------- derived data ---------------------------------------------- */

  StudioCalendar.prototype.filtered = function () {
    var f = this.state.filter;
    return f ? this.state.sessions.filter(function (s) { return s.pid === f; }) : this.state.sessions;
  };
  StudioCalendar.prototype.visible = function (r) {
    return this.filtered().filter(function (s) { return s.day >= r.start && s.day <= r.end; });
  };

  StudioCalendar.prototype.seatText = function (s) {
    var t = this.text;
    if (!s.open) return { text: t.soldOut, cls: 'is-out' };
    var below = parseInt(this.cfg.seatsShowBelow, 10) || 0;
    var cls = s.pool <= LOW_SEATS ? 'is-low' : '';
    if (below <= 0 || s.pool < below) {
      return { text: s.pool === 1 ? t.oneSeat : fill(t.seats, { n: s.pool }), cls: cls };
    }
    return { text: t.available, cls: '' };
  };

  /* ---------- rendering ------------------------------------------------- */

  StudioCalendar.prototype.buildChips = function () {
    var self = this, box = this.ui.chips;
    box.innerHTML = '';
    if (this.state.status !== 'ready') { box.hidden = true; return; }
    // Only workshops that have a session to show; a chip that can only ever
    // produce an empty calendar is noise.
    var has = {};
    this.state.sessions.forEach(function (s) { has[s.pid] = true; });
    var list = this.products.filter(function (p) { return has[p.id]; });
    if (list.length < 2) { box.hidden = true; return; }

    var all = el('button', 'studio-calendar__chip', this.text.allWorkshops);
    all.type = 'button';
    all.setAttribute('data-sc-chip', '');
    all.setAttribute('aria-pressed', 'true');
    all.setAttribute('aria-controls', this.bodyId);
    box.appendChild(all);
    list.forEach(function (p) {
      var b = el('button', 'studio-calendar__chip');
      b.type = 'button';
      b.setAttribute('data-sc-chip', p.id);
      b.setAttribute('aria-pressed', 'false');
      b.setAttribute('aria-controls', self.bodyId);
      b.style.setProperty('--sc-color', 'var(--sc-c' + p.color + ')');
      var dot = el('span', 'studio-calendar__dot');
      dot.setAttribute('aria-hidden', 'true');
      b.appendChild(dot);
      b.appendChild(document.createTextNode(p.name));
      box.appendChild(b);
    });
    box.hidden = false;
  };

  StudioCalendar.prototype.render = function () {
    var st = this.state, t = this.text, ui = this.ui;
    this.clamp();
    var r = this.range();
    var week = st.view === 'week';

    ui.views.forEach(function (b) { b.setAttribute('aria-pressed', String(b.getAttribute('data-sc-view') === st.view)); });
    ui.label.textContent = week ? this.weekLabel(r) : fmtDay(r.start, { month: 'long', year: 'numeric' });
    ui.prev.setAttribute('aria-label', week ? t.prevWeek : t.prevMonth);
    ui.next.setAttribute('aria-label', week ? t.nextWeek : t.nextMonth);
    ui.today.textContent = week ? t.thisWeek : t.thisMonth;

    if (st.status === 'loading') {
      ui.prev.disabled = ui.next.disabled = ui.today.disabled = true;
      ui.views.forEach(function (b) { b.disabled = true; });
      ui.status.textContent = t.loading;
      ui.body.setAttribute('aria-busy', 'true');
      return;
    }
    ui.views.forEach(function (b) { b.disabled = false; });
    ui.body.removeAttribute('aria-busy');

    if (st.status === 'error') {
      this.showFallback();
      return;
    }

    ui.prev.disabled = !this.canShift(-1);
    ui.next.disabled = !this.canShift(1);
    ui.today.disabled = this.today >= r.start && this.today <= r.end;

    var list = this.visible(r);
    ui.body.innerHTML = '';
    if (week) this.renderWeek(r, list); else this.renderMonth(r, list);

    var n = list.length;
    var month = fmtDay(r.start, { month: 'long' });
    ui.status.textContent = n === 0
      ? (week ? t.emptyWeek : t.emptyMonth)
      : week ? (n === 1 ? t.countWeekOne : fill(t.countWeek, { n: n }))
        : (n === 1 ? fill(t.countMonthOne, { month: month }) : fill(t.countMonth, { n: n, month: month }));

    ui.zone.textContent = fill(t.zone, { zone: zoneName(this.tz) });
    this.renderNote();
  };

  StudioCalendar.prototype.weekLabel = function (r) {
    var a = dparts(r.start), b = dparts(r.end);
    if (a[0] !== b[0]) {
      return fmtDay(r.start, { month: 'short', day: 'numeric', year: 'numeric' }) + ' – ' + fmtDay(r.end, { month: 'short', day: 'numeric', year: 'numeric' });
    }
    if (a[1] !== b[1]) {
      return fmtDay(r.start, { month: 'short', day: 'numeric' }) + ' – ' + fmtDay(r.end, { month: 'short', day: 'numeric' }) + ', ' + b[0];
    }
    return fmtDay(r.start, { month: 'short', day: 'numeric' }) + ' – ' + b[2] + ', ' + b[0];
  };

  StudioCalendar.prototype.sessionCard = function (s, withImage) {
    var p = this.byId[s.pid], tz = this.tz;
    var li = el('li', 'studio-calendar__item');
    var a = el('a', 'studio-calendar__session' + (s.open ? '' : ' is-sold-out'));
    a.href = p.url + this.anchor;
    a.setAttribute('data-sc-session', p.id);
    a.setAttribute('data-sc-date', s.day);
    a.style.setProperty('--sc-color', 'var(--sc-c' + p.color + ')');

    var time = fmtTime(s.start, tz) + (s.finish ? ' – ' + fmtTime(s.finish, tz) : '');
    var dur = s.finish ? fmtDuration(new Date(s.finish) - new Date(s.start)) : '';
    var seats = this.seatText(s);
    // One clear sentence for screen readers instead of the visual fragments.
    a.setAttribute('aria-label', [p.name, fmtDay(s.day, { weekday: 'long', month: 'long', day: 'numeric' }), time.replace(' – ', ' to '), dur, seats.text].filter(Boolean).join(', '));

    if (withImage && p.image) {
      var img = el('img', 'studio-calendar__thumb');
      img.src = p.image;
      img.alt = '';
      img.width = 48;
      img.height = 48;
      img.loading = 'lazy';
      a.appendChild(img);
    }
    var body = el('span', 'studio-calendar__session-body');
    body.appendChild(el('span', 'studio-calendar__time', time));
    body.appendChild(el('span', 'studio-calendar__name', p.name));
    var meta = el('span', 'studio-calendar__meta');
    if (dur) meta.appendChild(el('span', 'studio-calendar__dur', dur));
    meta.appendChild(el('span', 'studio-calendar__seats' + (seats.cls ? ' ' + seats.cls : ''), seats.text));
    body.appendChild(meta);
    a.appendChild(body);
    li.appendChild(a);
    return li;
  };

  // One row per day that has sessions; empty days are left out rather than
  // shown as seven mostly-blank columns.
  StudioCalendar.prototype.renderWeek = function (r, list) {
    if (!list.length) { this.ui.body.appendChild(this.emptyPanel(r)); return; }
    var self = this, ol = el('ol', 'studio-calendar__week');
    for (var i = 0; i < 7; i++) {
      var k = addDays(r.start, i);
      var items = list.filter(function (s) { return s.day === k; });
      if (!items.length) continue;
      var li = el('li', 'studio-calendar__day');
      if (k === this.today) li.classList.add('is-today');
      var head = el('h4', 'studio-calendar__day-head');
      head.appendChild(el('span', 'studio-calendar__day-dow', fmtDay(k, { weekday: 'short' })));
      head.appendChild(el('span', 'studio-calendar__day-date', fmtDay(k, { month: 'short', day: 'numeric' })));
      li.appendChild(head);
      var ul = el('ul', 'studio-calendar__sessions');
      items.forEach(function (s) { ul.appendChild(self.sessionCard(s, true)); });
      li.appendChild(ul);
      ol.appendChild(li);
    }
    this.ui.body.appendChild(ol);
  };

  StudioCalendar.prototype.renderMonth = function (r, list) {
    var self = this, t = this.text, tz = this.tz;
    var byDay = {};
    list.forEach(function (s) { (byDay[s.day] = byDay[s.day] || []).push(s); });
    var days = Object.keys(byDay).sort();

    // The day whose sessions are listed under the grid: the one picked, else
    // the first day in view that has any.
    var sel = this.state.selected;
    if (!sel || !byDay[sel]) sel = days[0] || null;
    this.state.selected = sel;
    var panelId = this.bodyId + '-day';

    var wrap = el('div', 'studio-calendar__month');
    var dows = el('div', 'studio-calendar__dows');
    dows.setAttribute('aria-hidden', 'true');
    for (var d = 0; d < 7; d++) dows.appendChild(el('span', null, fmtDay(addDays(weekOf(r.start), d), { weekday: 'short' })));
    wrap.appendChild(dows);

    var grid = el('ol', 'studio-calendar__grid');
    for (var pad = weekday(r.start); pad > 0; pad--) {
      var gap = el('li');
      gap.setAttribute('aria-hidden', 'true');
      grid.appendChild(gap);
    }
    for (var k = r.start; k <= r.end; k = addDays(k, 1)) {
      var items = byDay[k] || [];
      var li = el('li');
      var cell;
      if (items.length) {
        cell = el('button', 'studio-calendar__cell');
        cell.type = 'button';
        cell.setAttribute('data-sc-day', k);
        cell.setAttribute('aria-pressed', String(k === sel));
        cell.setAttribute('aria-controls', panelId);
        cell.setAttribute('aria-label', fmtDay(k, { weekday: 'long', month: 'long', day: 'numeric' }) + ', ' +
          (items.length === 1 ? t.sessionsOne : fill(t.sessions, { n: items.length })));
      } else {
        cell = el('div', 'studio-calendar__cell');
      }
      if (k === this.today) cell.classList.add('is-today');
      if (k < this.today) cell.classList.add('is-past');
      var num = el('span', 'studio-calendar__num', String(dparts(k)[2]));
      if (items.length) num.setAttribute('aria-hidden', 'true');   // the button's label already says it
      cell.appendChild(num);

      if (items.length) {
        var pills = el('span', 'studio-calendar__pills');
        pills.setAttribute('aria-hidden', 'true');
        items.slice(0, PILLS_PER_DAY).forEach(function (s) {
          var pill = el('span', 'studio-calendar__pill', fmtShortTime(s.start, tz) + ' ' + self.byId[s.pid].name);
          pill.style.setProperty('--sc-color', 'var(--sc-c' + self.byId[s.pid].color + ')');
          pills.appendChild(pill);
        });
        if (items.length > PILLS_PER_DAY) pills.appendChild(el('span', 'studio-calendar__more', fill(t.more, { n: items.length - PILLS_PER_DAY })));
        cell.appendChild(pills);

        var dots = el('span', 'studio-calendar__dots');
        dots.setAttribute('aria-hidden', 'true');
        items.slice(0, DOTS_PER_DAY).forEach(function (s) {
          var dot = el('span', 'studio-calendar__dot');
          dot.style.setProperty('--sc-color', 'var(--sc-c' + self.byId[s.pid].color + ')');
          dots.appendChild(dot);
        });
        cell.appendChild(dots);
      }
      li.appendChild(cell);
      grid.appendChild(li);
    }
    wrap.appendChild(grid);

    if (!list.length) {
      wrap.appendChild(this.emptyPanel(r));
    } else if (sel) {
      var panel = el('div', 'studio-calendar__panel');
      panel.id = panelId;
      panel.appendChild(el('h4', 'studio-calendar__panel-head', fmtDay(sel, { weekday: 'long', month: 'long', day: 'numeric' })));
      var ul = el('ul', 'studio-calendar__sessions');
      byDay[sel].forEach(function (s) { ul.appendChild(self.sessionCard(s, true)); });
      panel.appendChild(ul);
      wrap.appendChild(panel);
    }
    this.ui.body.appendChild(wrap);
  };

  StudioCalendar.prototype.emptyPanel = function (r) {
    var t = this.text, st = this.state;
    var box = el('div', 'studio-calendar__empty');
    box.appendChild(el('p', 'studio-calendar__empty-title', st.view === 'week' ? t.emptyWeek : t.emptyMonth));

    // The next session the arrows can actually reach. Sessions in the spill
    // days after the last month are loaded for the week view, but the month
    // view stops at that month.
    var limit = st.view === 'month' ? lastOfMonth(this.maxMonth) : this.lastDay;
    var next = null, all = this.filtered();
    for (var i = 0; i < all.length; i++) {
      if (all[i].day > r.end && all[i].day <= limit) { next = all[i]; break; }
    }
    if (next) {
      var p = el('p');
      var b = el('button', 'studio-calendar__jump', fill(t.next, { date: fmtDay(next.day, { weekday: 'short', month: 'short', day: 'numeric' }) }));
      b.type = 'button';
      b.setAttribute('data-sc-jump', next.day);
      p.appendChild(b);
      box.appendChild(p);
    } else if (!st.filter) {
      box.appendChild(el('p', null, t.noneAhead));
    }
    if (st.filter) {
      var f = el('p', null, fill(t.filtered, { name: this.byId[st.filter].name }) + ' ');
      var all2 = el('button', 'studio-calendar__jump studio-calendar__jump--quiet', t.showAll);
      all2.type = 'button';
      all2.setAttribute('data-sc-show-all', '');
      f.appendChild(all2);
      box.appendChild(f);
    }
    return box;
  };

  // Some workshops failed while others loaded: say which, and link them.
  StudioCalendar.prototype.renderNote = function () {
    var self = this, note = this.ui.note;
    var failed = this.state.failed.map(function (id) { return self.byId[id]; }).filter(Boolean);
    note.innerHTML = '';
    if (!failed.length) { note.hidden = true; return; }
    note.appendChild(document.createTextNode(fill(this.text.partial, { names: failed.map(function (p) { return p.name; }).join(', ') }) + ' '));
    failed.forEach(function (p, i) {
      if (i) note.appendChild(document.createTextNode(' · '));
      var a = el('a', null, p.name);
      a.href = p.url;
      note.appendChild(a);
    });
    note.hidden = false;
  };

  StudioCalendar.prototype.showFallback = function () {
    var fb = this.ui.fallback;
    if (!fb) return;
    // Link only to workshops BookThatApp manages, when we know which they are.
    var managed = this.products.some(function (p) { return p.bta; });
    qsa(fb, '[data-sc-bta]').forEach(function (li) {
      li.hidden = managed && li.getAttribute('data-sc-bta') !== 'true';
    });
    this.ui.wrap.hidden = true;
    fb.hidden = false;
  };

  /* ---------- boot ------------------------------------------------------ */

  function initAll(scope) {
    qsa(scope || document, '[data-studio-calendar]').forEach(function (root) {
      if (root.__masStudioCalendar) return;
      try {
        root.__masStudioCalendar = new StudioCalendar(root);
      } catch (e) {
        if (window.console) console.error('[MAS studio calendar] failed to start', e);
        var fb = qs(root, '[data-sc-fallback]'), ui = qs(root, '[data-sc-ui]');
        if (fb) fb.hidden = false;
        if (ui) ui.hidden = true;
      }
    });
  }

  window.MASStudioCalendar = { init: initAll, StudioCalendar: StudioCalendar };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(); });
  else initAll();
  // Theme editor: a re-rendered section brings a fresh root.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
